import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { Label } from "@/components/atoms/Label";
import { GoogleIcon } from "@/components/atoms/GoogleIcon";
import { AuthenticationService } from "@/modules/identity/services/AuthenticationService";
import { loginSchema, type LoginFormValues } from "@/modules/identity/schemas/authSchemas";
import { toast } from "@/hooks/use-toast";
import { ROUTES } from "@/constants/routes";
import { getFirebaseErrorMessage } from "@/utils/firebaseErrors";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({ resolver: zodResolver(loginSchema) });

  const redirectTo = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? ROUTES.home;

  async function onSubmit(values: LoginFormValues) {
    try {
      await AuthenticationService.signInWithEmail(values.email, values.password);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      toast({ variant: "destructive", title: "Não foi possível entrar", description: getFirebaseErrorMessage(error) });
    }
  }

  async function handleGoogleLogin() {
    setIsGoogleLoading(true);
    try {
      await AuthenticationService.signInWithGoogle();
      navigate(redirectTo, { replace: true });
    } catch (error) {
      toast({ variant: "destructive", title: "Não foi possível entrar com o Google", description: getFirebaseErrorMessage(error) });
    } finally {
      setIsGoogleLoading(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className="mx-auto w-full max-w-sm space-y-6 rounded-2xl bg-card p-6 shadow-elevated"
    >
      <div className="space-y-1 text-center">
        <h1 className="font-display text-xl font-bold">Bem-vindo de volta</h1>
        <p className="text-sm text-muted-foreground">Entre para acompanhar seus bolões</p>
      </div>

      <Button variant="outline" className="w-full" onClick={handleGoogleLogin} isLoading={isGoogleLoading}>
        <GoogleIcon /> Continuar com Google
      </Button>

      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" />
        ou entre com e-mail
        <div className="h-px flex-1 bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="space-y-1.5">
          <Label htmlFor="email">E-mail</Label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input id="email" type="email" placeholder="voce@email.com" className="pl-10" error={errors.email?.message} {...register("email")} />
          </div>
          {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Senha</Label>
            <Link to={ROUTES.forgotPassword} className="text-xs font-medium text-primary hover:underline">
              Esqueceu a senha?
            </Link>
          </div>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              className="pl-10 pr-10"
              error={errors.password?.message}
              {...register("password")}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
        </div>

        <Button type="submit" className="w-full" isLoading={isSubmitting}>
          Entrar
        </Button>
      </form>

      <p className="text-center text-sm text-muted-foreground">
        Não tem conta?{" "}
        <Link to={ROUTES.register} className="font-semibold text-primary hover:underline">
          Criar conta
        </Link>
      </p>
    </motion.div>
  );
}
